<template>
	<section>
		<loader :dialog="loaderDialog"></loader>
		<floating-alert :message="alertMessage" :show="alertShow" :type="alertType"></floating-alert>

		<!-- ─── Stats ─── -->
		<v-row class="mb-4">
			<v-col cols="12" sm="6" lg="3">
				<div class="exp-kpi-card" style="border-top:3px solid #c62828;">
					<div class="exp-kpi-icon" style="background:#c62828;">
						<v-icon size="18" color="white">mdi-calendar-today</v-icon>
					</div>
					<div>
						<h3 class="exp-kpi-val">
							<span v-if="statsLoading">—</span>
							<span v-else>{{ (expStats.today || 0).toLocaleString('fr') }}</span>
						</h3>
						<p class="exp-kpi-lbl">Aujourd'hui (FCFA)</p>
					</div>
				</div>
			</v-col>
			<v-col cols="12" sm="6" lg="3">
				<div class="exp-kpi-card" style="border-top:3px solid #1565c0;">
					<div class="exp-kpi-icon" style="background:#1565c0;">
						<v-icon size="18" color="white">mdi-calendar-week</v-icon>
					</div>
					<div>
						<h3 class="exp-kpi-val">
							<span v-if="statsLoading">—</span>
							<span v-else>{{ (expStats.week || 0).toLocaleString('fr') }}</span>
						</h3>
						<p class="exp-kpi-lbl">Cette semaine (FCFA)</p>
					</div>
				</div>
			</v-col>
			<v-col cols="12" sm="6" lg="3">
				<div class="exp-kpi-card" style="border-top:3px solid #00695c;">
					<div class="exp-kpi-icon" style="background:#00695c;">
						<v-icon size="18" color="white">mdi-calendar-month</v-icon>
					</div>
					<div>
						<h3 class="exp-kpi-val">
							<span v-if="statsLoading">—</span>
							<span v-else>{{ (expStats.month || 0).toLocaleString('fr') }}</span>
						</h3>
						<p class="exp-kpi-lbl">Ce mois (FCFA)</p>
					</div>
				</div>
			</v-col>
			<v-col cols="12" sm="6" lg="3">
				<div class="exp-kpi-card" style="border-top:3px solid #111827;">
					<div class="exp-kpi-icon" style="background:#111827;">
						<v-icon size="18" color="white">mdi-calendar</v-icon>
					</div>
					<div>
						<h3 class="exp-kpi-val">
							<span v-if="statsLoading">—</span>
							<span v-else>{{ (expStats.year || 0).toLocaleString('fr') }}</span>
						</h3>
						<p class="exp-kpi-lbl">Cette année (FCFA)</p>
					</div>
				</div>
			</v-col>
		</v-row>

		<!-- ─── Table ─── -->
		<v-card elevation="2" rounded="xl">
			<div class="exp-header">
				<div class="exp-header-left">
					<div class="exp-icon-box">
						<v-icon size="18" color="white">mdi-cash-minus</v-icon>
					</div>
					<div>
						<p class="exp-title">Dépenses</p>
						<p class="exp-subtitle">Suivi des dépenses</p>
					</div>
				</div>

				<v-text-field
					v-model="search"
					prepend-inner-icon="mdi-magnify"
					placeholder="Rechercher..."
					outlined dense hide-details
					class="exp-search"
					color="blue darken-2"
				></v-text-field>

				<v-btn depressed rounded class="exp-add-btn" @click="dialog = true">
					<v-icon left small>mdi-plus</v-icon>
					Nouvelle dépense
				</v-btn>
			</div>

			<v-divider></v-divider>

			<!-- Info traçabilité -->
			<div class="exp-info-bar">
				<v-icon size="14" color="#1565c0" class="mr-1">mdi-information-outline</v-icon>
				<span>Les dépenses ne peuvent plus être modifiées ni supprimées. En cas d'erreur, utilisez <strong>Ajustement</strong> pour enregistrer une annulation comptable traçable.</span>
			</div>

			<v-divider></v-divider>

			<v-data-table
				:headers="headers"
				:items="depenses"
				:server-items-length="totalItems"
				:options.sync="options"
				:loading="loading"
				loading-text="Chargement en cours..."
				class="exp-table"
				:footer-props="{ 'items-per-page-options': [10, 25, 50], 'items-per-page-text': 'Lignes par page', 'items-per-page-all-text': 'Tout' }"
			>
				<template v-slot:[`item.reason`]="{ item }">
					<div class="exp-reason-cell">
						<v-chip v-if="item.isAdjustment" x-small color="#fef3c7" text-color="#92400e" class="mr-2">Annulation</v-chip>
						<span>{{ item.reason }}</span>
					</div>
				</template>
				<template v-slot:[`item.amount`]="{ item }">
					<span :class="item.isAdjustment ? 'exp-amount-adj' : 'exp-amount-val'">
						{{ item.amount > 0 ? '' : '' }}{{ item.amount.toLocaleString('fr') }} FCFA
					</span>
				</template>
				<template v-slot:[`item.date`]="{ item }">{{ formatDate(item.date) }}</template>
				<template v-slot:[`item.actions`]="{ item }">
					<v-tooltip bottom :disabled="item.isAdjustment">
						<template v-slot:activator="{ on, attrs }">
							<span v-bind="attrs" v-on="on">
								<v-btn icon small :disabled="item.isAdjustment" @click="openAdjustment(item)">
									<v-icon size="17" :color="item.isAdjustment ? '#d1d5db' : '#f59e0b'">mdi-swap-horizontal</v-icon>
								</v-btn>
							</span>
						</template>
						<span>Créer un ajustement comptable</span>
					</v-tooltip>
				</template>
				<template v-slot:[`footer.page-text`]="items">{{ items.pageStart }}-{{ items.pageStop }} sur {{ items.itemsLength }}</template>
				<template v-slot:no-data>
					<v-btn text color="primary" @click="initialize">Actualiser</v-btn>
				</template>
			</v-data-table>
		</v-card>

		<!-- ─── Dialog: Nouvelle dépense ─── -->
		<v-dialog v-model="dialog" max-width="480px">
			<v-card rounded="xl" class="pa-2">
				<div class="form-dialog-header">
					<div class="form-dialog-icon" style="background:#c62828;">
						<v-icon size="14" color="white">mdi-cash-minus</v-icon>
					</div>
					<div>
						<p class="form-dialog-title">Nouvelle dépense</p>
						<p class="form-dialog-sub">Dépense</p>
					</div>
					<v-spacer></v-spacer>
					<v-btn icon small @click="closeDialog"><v-icon color="#6b7280">mdi-close</v-icon></v-btn>
				</div>
				<v-divider></v-divider>
				<v-card-text class="pt-5">
					<v-form ref="form" v-model="valid" lazy-validation>
						<v-text-field
							v-model="form.reason"
							label="Motif"
							outlined dense rounded
							prepend-inner-icon="mdi-text"
							:rules="[v => !!v || 'Champ requis']"
							hide-details="auto" class="mb-4"
						></v-text-field>
						<v-text-field
							v-model.number="form.amount"
							label="Montant"
							outlined dense rounded
							prepend-inner-icon="mdi-cash"
							suffix="FCFA" type="number" min="0"
							:rules="[v => (v !== '' && v !== null && v !== undefined) || 'Champ requis', v => v > 0 || 'Montant invalide']"
							hide-details="auto" class="mb-4"
						></v-text-field>
						<v-menu v-model="dateMenu" :close-on-content-click="false">
							<template v-slot:activator="{ on, attrs }">
								<v-text-field
									v-model="form.date"
									label="Date"
									outlined dense rounded
									prepend-inner-icon="mdi-calendar"
									readonly
									:rules="[v => !!v || 'Champ requis']"
									hide-details="auto" class="mb-4"
									v-bind="attrs" v-on="on"
								></v-text-field>
							</template>
							<v-date-picker locale="fr" color="#c62828" v-model="form.date" @input="dateMenu = false"></v-date-picker>
						</v-menu>
						<v-textarea
							v-model="form.description"
							label="Description (optionnel)"
							outlined dense rounded
							prepend-inner-icon="mdi-note-text"
							rows="3" hide-details
						></v-textarea>
					</v-form>
				</v-card-text>
				<v-card-actions class="pa-4 pt-2">
					<v-btn text rounded color="grey darken-1" @click="closeDialog">Annuler</v-btn>
					<v-spacer></v-spacer>
					<v-btn :disabled="!valid" depressed rounded class="exp-save-btn" @click="save">Enregistrer</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- ─── Dialog: Ajustement comptable ─── -->
		<v-dialog v-model="dialogAdj" max-width="480px">
			<v-card rounded="xl" class="pa-2">
				<div class="form-dialog-header">
					<div class="form-dialog-icon" style="background:#f59e0b;">
						<v-icon size="14" color="white">mdi-swap-horizontal</v-icon>
					</div>
					<div>
						<p class="form-dialog-title">Ajustement comptable</p>
						<p class="form-dialog-sub">Annulation / correction</p>
					</div>
					<v-spacer></v-spacer>
					<v-btn icon small @click="dialogAdj = false"><v-icon color="#6b7280">mdi-close</v-icon></v-btn>
				</div>
				<v-divider></v-divider>
				<v-card-text class="pt-4">
					<!-- Dépense originale -->
					<div v-if="adjTarget" class="adj-original">
						<p class="adj-orig-label">Dépense concernée</p>
						<p class="adj-orig-reason">{{ adjTarget.reason }}</p>
						<div class="adj-orig-row">
							<span>{{ adjTarget.amount.toLocaleString('fr') }} FCFA</span>
							<span class="adj-orig-date">· {{ formatDate(adjTarget.date) }}</span>
						</div>
					</div>
					<v-divider class="my-3"></v-divider>
					<p class="adj-info">
						<v-icon size="14" color="#1565c0">mdi-information-outline</v-icon>
						Un ajustement crée une écriture comptable inverse (montant négatif) pour annuler cette dépense tout en conservant l'historique complet.
					</p>
					<v-form ref="adjForm" v-model="adjValid" lazy-validation class="mt-3">
						<v-menu v-model="adjDateMenu" :close-on-content-click="false">
							<template v-slot:activator="{ on, attrs }">
								<v-text-field
									v-model="adjForm.date"
									label="Date de l'ajustement"
									outlined dense rounded
									prepend-inner-icon="mdi-calendar"
									readonly
									:rules="[v => !!v || 'Champ requis']"
									hide-details="auto" class="mb-4"
									v-bind="attrs" v-on="on"
								></v-text-field>
							</template>
							<v-date-picker locale="fr" color="#f59e0b" v-model="adjForm.date" @input="adjDateMenu = false"></v-date-picker>
						</v-menu>
						<v-textarea
							v-model="adjForm.description"
							label="Motif de l'ajustement (optionnel)"
							outlined dense rounded
							prepend-inner-icon="mdi-note-text"
							rows="3" hide-details
						></v-textarea>
					</v-form>
				</v-card-text>
				<v-card-actions class="pa-4 pt-2">
					<v-btn text rounded color="grey darken-1" @click="dialogAdj = false">Annuler</v-btn>
					<v-spacer></v-spacer>
					<v-btn :disabled="!adjValid" depressed rounded class="adj-btn" @click="saveAdjustment">Confirmer l'ajustement</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</section>
</template>

<script>
import Loader from "@/components/Loader.vue";
import FloatingAlert from "@/components/FloatingAlert.vue";
import service from "../services";
export default {
	components: { Loader, FloatingAlert },
	data: () => ({
		loaderDialog: false,
		alertMessage: "",
		alertShow: false,
		alertType: "",
		dialog: false,
		dialogAdj: false,
		adjTarget: null,
		adjDateMenu: false,
		adjValid: false,
		adjForm: { date: "", description: "" },
		dateMenu: false,
		valid: false,
		loading: false,
		statsLoading: true,
		search: "",
		searchTimeout: null,
		options: {},
		totalItems: 0,
		depenses: [],
		expStats: { today: 0, week: 0, month: 0, year: 0 },
		form: { reason: "", amount: 0, date: "", description: "" },
		defaultForm: { reason: "", amount: 0, date: "", description: "" },
		headers: [
			{ text: "Motif", value: "reason", sortable: false },
			{ text: "Montant", value: "amount", sortable: false },
			{ text: "Date", value: "date", sortable: false },
			{ text: "Description", value: "description", sortable: false },
			{ text: "Ajustement", value: "actions", sortable: false, width: "80px" },
		],
	}),
	watch: {
		options: { handler() { this.initialize(); }, deep: true },
		search() {
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {
				if (this.options.page !== 1) this.options.page = 1;
				else this.initialize();
			}, 400);
		},
		dialog(val) { val || this.closeDialog(); },
	},
	created() { this.loadStats(); },
	methods: {
		initialize() {
			this.loading = true;
			const page = this.options.page || 1;
			const limit = this.options.itemsPerPage || 10;
			service.getExpenses({ page, limit, search: this.search })
				.then((r) => {
					this.depenses = r.data?.depenses || [];
					this.totalItems = (r.data?.totalPages ?? 0) * limit;
					this.loading = false;
				})
				.catch((e) => { console.log(e); this.loading = false; });
		},
		loadStats() {
			this.statsLoading = true;
			service.getExpenseStats()
				.then((r) => { this.expStats = r.data || {}; this.statsLoading = false; })
				.catch((e) => { console.log(e); this.statsLoading = false; });
		},
		formatDate(d) {
			if (!d) return "—";
			return new Intl.DateTimeFormat("fr-FR").format(new Date(d));
		},
		manageAlert(show, type, msg) { this.alertShow = show; this.alertType = type; this.alertMessage = msg; },
		openAdjustment(item) {
			this.adjTarget = item;
			this.adjForm = { date: new Date().toISOString().split('T')[0], description: "" };
			this.dialogAdj = true;
			this.$nextTick(() => { if (this.$refs.adjForm) this.$refs.adjForm.resetValidation(); });
		},
		saveAdjustment() {
			if (!this.$refs.adjForm.validate()) return;
			this.loaderDialog = true;
			service.createExpenseAdjustment(this.adjTarget._id, this.adjForm)
				.then(() => {
					this.loaderDialog = false;
					this.dialogAdj = false;
					this.adjTarget = null;
					this.manageAlert(true, "success", "Ajustement comptable enregistré !");
					this.initialize();
					this.loadStats();
				})
				.catch((e) => { this.loaderDialog = false; this.manageAlert(true, "error", e?.message || "Erreur"); });
		},
		closeDialog() {
			this.dialog = false;
			this.$nextTick(() => { this.form = Object.assign({}, this.defaultForm); if (this.$refs.form) this.$refs.form.resetValidation(); });
		},
		save() {
			if (!this.$refs.form.validate()) return;
			this.loaderDialog = true;
			service.createExpense(this.form)
				.then(() => {
					this.loaderDialog = false;
					this.manageAlert(true, "success", "Dépense enregistrée !");
					this.closeDialog();
					this.initialize();
					this.loadStats();
				}).catch((e) => { this.loaderDialog = false; this.manageAlert(true, "error", e?.message || "Erreur"); });
		},
	},
};
</script>

<style scoped>
/* KPI */
.exp-kpi-card { background: white; border-radius: 14px; padding: 16px; box-shadow: 0 2px 12px rgba(0,0,0,0.06); display: flex; align-items: center; gap: 14px; font-family: Montserrat, sans-serif; }
.exp-kpi-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.exp-kpi-val { font-size: 1.4rem; font-weight: 800; color: #111827; margin: 0 0 2px; }
.exp-kpi-lbl { font-size: 0.68rem; font-weight: 600; color: #9ca3af; margin: 0; text-transform: uppercase; letter-spacing: 0.5px; }
/* Header */
.exp-header { display: flex; align-items: center; padding: 18px 20px 14px; gap: 14px; flex-wrap: wrap; }
.exp-header-left { display: flex; align-items: center; gap: 14px; flex: 1; min-width: 200px; }
.exp-icon-box { width: 38px; height: 38px; border-radius: 10px; background: #c62828; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.exp-title { font-size: 0.92rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.exp-subtitle { font-size: 0.72rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.exp-search { max-width: 240px; }
.exp-add-btn { background: #c62828 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; box-shadow: 0 4px 12px rgba(198,40,40,0.28) !important; letter-spacing: 0.3px; }
.exp-info-bar { display: flex; align-items: flex-start; gap: 6px; padding: 10px 20px; background: #eff6ff; font-family: Montserrat, sans-serif; font-size: 0.72rem; color: #1565c0; }
.exp-table { font-family: Montserrat, sans-serif; }
.exp-amount-val { font-weight: 700; color: #c62828; }
.exp-amount-adj { font-weight: 700; color: #059669; }
.exp-reason-cell { display: flex; align-items: center; gap: 4px; }
/* Form dialog */
.form-dialog-header { display: flex; align-items: center; gap: 14px; padding: 18px 20px 14px; }
.form-dialog-icon { width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; }
.form-dialog-title { font-size: 0.88rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.form-dialog-sub { font-size: 0.68rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.exp-save-btn { background: #c62828 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; box-shadow: 0 4px 12px rgba(198,40,40,0.28) !important; letter-spacing: 0.3px; }
/* Ajustement */
.adj-original { background: #f9fafb; border: 1.5px solid #e5e7eb; border-radius: 10px; padding: 12px 14px; font-family: Montserrat, sans-serif; }
.adj-orig-label { font-size: 0.65rem; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 1px; margin: 0 0 4px; }
.adj-orig-reason { font-size: 0.88rem; font-weight: 700; color: #111827; margin: 0 0 4px; }
.adj-orig-row { display: flex; align-items: center; gap: 6px; font-size: 0.82rem; font-weight: 600; color: #c62828; }
.adj-orig-date { color: #6b7280; font-weight: 400; }
.adj-info { font-size: 0.72rem; color: #1565c0; font-family: Montserrat, sans-serif; background: #eff6ff; border-radius: 8px; padding: 8px 10px; display: flex; align-items: flex-start; gap: 6px; margin: 0; }
.adj-btn { background: #f59e0b !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; }
</style>
